import React, { useState, useEffect, useCallback } from "react";
import makeStyles from "@mui/styles/makeStyles";

import { notBlank } from "../../../util/ext";
import { filter, isEmpty, join, test } from "ramda";

import Container from "@mui/material/Container";
import SearchIcon from "@mui/icons-material/Search";
import ListItem from "@mui/material/ListItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";

import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import OptionalFieldMenuItem from "./OptionalFieldMenuItem";

const useStyles = makeStyles(theme => ({
  search: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: "4px",
    position: "relative",
    width: "95%",
    display: "flex",
    alignItems: "center",
    margin: "auto",
  },

  searchIcon: {
    color: theme.palette.primary.darker,
  },

  inputRoot: {
    color: theme.palette.primary.darker,
    flexGrow: 1,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
  },

  inputFocused: {
    borderColor: theme.palette.primary.brandPrimary,
  },

  outline: {
    borderColor: "transparent",
  },

  inputInput: {
    width: "100%",
    padding: theme.spacing(2.5),
  },

  mediaContainer: {
    height: "330px",
    overflowY: "auto",
    marginTop: theme.spacing(2.5),
  },

  menuItem: {
    height: 55,
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.primary.brandPrimary,
    },
  },

  footer: {
    padding: theme.spacing(1.5, 2, 0, 2),
  },

  selectedCount: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
  },

  moreButtonContainer: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  moreButton: {
    width: "100%",
  },
}));

const OptionalFieldMenu = props => {
  const classes = useStyles();
  const { handleItemClick, optionalFields } = props;

  const [filteredList, setFilteredList] = useState();
  const [searchTerm, setSearchTerm] = useState("");

  const onSearchChange = useCallback(
    event => {
      setSearchTerm(event.target.value);
      const searchRegEx = new RegExp(event.target.value, "ig");
      const filteredList = filter(
        optionalField => test(searchRegEx, optionalField.label),
        optionalFields,
      );
      setFilteredList(filteredList);
    },
    [optionalFields],
  );

  //-- Handle loading styling
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (notBlank(optionalFields)) {
      setFilteredList(optionalFields);
      setLoading(false);
    }
  }, [optionalFields]);

  return (
    <Container disableGutters style={{ height: loading ? "300px" : "100%" }}>
      <div className={classes.search}>
        <OutlinedInput
          autoFocus
          placeholder="Search…"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
            notchedOutline: classes.outline,
            focused: classes.inputFocused,
          }}
          onChange={onSearchChange}
          inputProps={{ "aria-label": "search" }}
          value={searchTerm}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon
                fontSize="large"
                classes={{ root: classes.searchIcon }}
              />
            </InputAdornment>
          }
        />
      </div>
      {!loading ? (
        <Container disableGutters classes={{ root: classes.mediaContainer }}>
          {isEmpty(filteredList) ? (
            <ListItem>
              <i>No results</i>
            </ListItem>
          ) : (
            filteredList.map(optionalField => (
              <OptionalFieldMenuItem
                key={`key-${join("-", optionalField.fieldName)}`}
                optionalField={optionalField}
                handleClick={handleItemClick}
              />
            ))
          )}
        </Container>
      ) : (
        <PanelLoadingIndicator />
      )}
    </Container>
  );
};

export default OptionalFieldMenu;
